import { type EpgDateEx } from '~/entities/epg/dates'
import { HALF_HOUR_MS, ONE_DAY_MS, ONE_HOUR_MS } from '~/shared/constants'

type EpgProgramForDayTabs = {
  actualStartMs: number
  actualStopMs: number
  startMs: number
  stopMs: number
  isLive: boolean
  isPast: boolean
  isCatchup?: boolean
  isEmpty?: boolean
}

export const filterProgramsByEpgSettings = <T extends EpgProgramForDayTabs>(
  programList: T[],
  epgSettings: { pastHours: number; futureHours: number },
  timeshiftTimeMs?: number
): T[] => {
  const now = Date.now() + (timeshiftTimeMs || 0)
  const { pastHours, futureHours } = epgSettings || {}
  const maxPastTime = now - pastHours * ONE_HOUR_MS
  const roundedMaxPastTime =
    Math.floor(maxPastTime / HALF_HOUR_MS) * HALF_HOUR_MS
  const maxFutureTime = now + futureHours * ONE_HOUR_MS
  const roundedMaxFutureTime =
    Math.ceil(maxFutureTime / HALF_HOUR_MS) * HALF_HOUR_MS

  return programList
    .filter((program) => {
      // filter programs according to epg settings

      // program started before max past time and ended after max past time (to show cards that was clipped (start time))
      const isStartedBeforeMaxPastAndEndAfterMaxPast =
        program.actualStartMs <= roundedMaxPastTime &&
        program.actualStopMs >= roundedMaxPastTime

      // all program cards that are in range of max past and max future time
      const isStartedAfterMaxPastEndsBeforeMaxFuture =
        program.actualStartMs >= roundedMaxPastTime &&
        program.actualStopMs <= roundedMaxFutureTime

      // program started before max future time and ended after max future time (to show cards that was clipped (end time))
      const isUpcomingBeforeMaxFuture =
        program.actualStartMs > now &&
        program.actualStartMs < roundedMaxFutureTime

      const isLiveProgram = program.isLive

      return (
        isStartedBeforeMaxPastAndEndAfterMaxPast ||
        isStartedAfterMaxPastEndsBeforeMaxFuture ||
        isUpcomingBeforeMaxFuture ||
        isLiveProgram
      )
    })
    .filter((program) => {
      // filter out past programs that are empty and not catchup
      const pastProgram = program.isPast
      const isCatchup = program.isCatchup
      const isEmpty = program.isEmpty
      if (pastProgram && !isCatchup && isEmpty) {
        return false
      }
      return true
    })
}

// set tvProgramList for day
export const setDayProgram = <T extends EpgProgramForDayTabs>(
  activeDay: EpgDateEx,
  programList: T[]
): (T & {
  isStartedTheDayBeforeEndedActiveDay: boolean
  isStartedActiveDayEndedTheNextDay: boolean
})[] => {
  const { year, month, day } = activeDay
  const activeDayStarted = new Date(year, month, day).getTime()
  const activeDayEnded = activeDayStarted + ONE_DAY_MS
  const dayBeforeActiveStarted = activeDayStarted - ONE_DAY_MS
  const dayAfterActiveEnded = activeDayEnded + ONE_DAY_MS

  const list = programList.map((item) => {
    return {
      ...item,
      isStartedTheDayBeforeEndedActiveDay:
        item.startMs === dayBeforeActiveStarted &&
        item.startMs < activeDayStarted &&
        item.stopMs > activeDayStarted &&
        item.stopMs < activeDayEnded,
      isStartedActiveDayEndedTheNextDay:
        item.startMs === activeDayStarted &&
        item.startMs < activeDayEnded &&
        item.stopMs > activeDayEnded &&
        item.stopMs < dayAfterActiveEnded,
    }
  })

  const activeProgramList = list.filter(
    ({
      isEmpty,
      startMs,
      isStartedTheDayBeforeEndedActiveDay,
      isStartedActiveDayEndedTheNextDay,
    }) => {
      if (isEmpty) {
        if (isStartedTheDayBeforeEndedActiveDay) return true
        if (isStartedActiveDayEndedTheNextDay) return false
      }
      return startMs >= activeDayStarted && startMs < activeDayEnded
    }
  )

  return activeProgramList
}
